import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import {
  VenueMapConfigPlaceholder,
  VenueMapUrl,
  VenueMapV3Url,
  VenueMapVersionPlaceholder,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionInfo } from 'src/WebApiController';

import { useCtrlPlusM } from './EventVenueMap.hooks';
import { VenueMapV3 } from './v3/VenueMapV3';
import { VenueMap } from './VenueMap';
import * as styles from './VenueMap.css';
import { VenueMapProps } from './VenueMapProps';

export type EventVenueMapProps = Omit<
  VenueMapProps,
  | 'selectedSections'
  | 'svgMapUrl'
  | 'venueConfigId'
  | 'availableSections'
  | 'focalPoint'
> & {
  selectedSectionIds?: number[];
  onToggleMirrors?: (sections: SectionInfo[], exclude?: boolean) => void;
};

export const EventVenueMap = ({
  selectedSectionIds,
  markedSectionIds,
  animatedSectionIds,
  onSectionClicked,
  onSectionHovered,
  onSectionTapAndHold,
  setSelectedSections,
  onToggleMirrors,
  staticInfoContent,
  staticInfoContentPosition,
  alwaysShowStaticInfo,
  infoIconContent,
  isZoomEnabled = true,
  hideControls = false,
  isPanEnabled = true,
  showDefaultMapColors,
  colorBandProps,
  getColor,
  useRowMap = false,
  isHeatMap = false,
  isWhiteBackground = false,
  isRelocatedStaticInfo = false,
  allowDrawLine = true,
  isCondensedView = false,
  cornerContent,
  statsContent,
  disableHoverEffect = false,
  mapActionContainerClassname,
  condensedControl = false,
}: EventVenueMapProps) => {
  const { venueMapInfo, isMapLoading, isGeneralAdmissionOnly } =
    useEventMapContext();

  useCtrlPlusM(markedSectionIds ?? selectedSectionIds ?? [], onToggleMirrors);

  const selectedSectionData = useMemo(() => {
    if (!venueMapInfo || !selectedSectionIds?.length) {
      return undefined;
    }

    return venueMapInfo.sections?.filter((s: SectionInfo) =>
      selectedSectionIds.includes(s.id)
    );
  }, [selectedSectionIds, venueMapInfo]);

  const shouldUseV3 = venueMapInfo?.hasRow;

  if (isMapLoading) {
    return (
      <div className={styles.venueMapUnavailableContainer}>
        <PosSpinner />
      </div>
    );
  }
  if (!venueMapInfo) {
    return (
      <div className={styles.venueMapUnavailableContainer}>
        <Content id={ContentId.MapUnavailable} />
      </div>
    );
  }

  return shouldUseV3 ? (
    <VenueMapV3
      svgMapUrl={VenueMapV3Url.replace(
        VenueMapConfigPlaceholder,
        venueMapInfo.venueCfgId.toString()
      ).replace(
        VenueMapVersionPlaceholder,
        (venueMapInfo.svgMapVer ?? 1).toString()
      )}
      useRowMap={useRowMap}
      venueConfigId={venueMapInfo.venueCfgId}
      focalPoint={venueMapInfo.focalPt}
      availableSections={venueMapInfo.sections ?? []}
      selectedSections={selectedSectionData}
      markedSectionIds={markedSectionIds}
      animatedSectionIds={animatedSectionIds}
      onSectionClicked={onSectionClicked}
      onSectionTapAndHold={onSectionTapAndHold}
      onSectionHovered={onSectionHovered}
      setSelectedSections={setSelectedSections}
      staticInfoContent={staticInfoContent}
      staticInfoContentPosition={staticInfoContentPosition}
      alwaysShowStaticInfo={alwaysShowStaticInfo}
      infoIconContent={infoIconContent}
      isGeneralAdmissionOnly={isGeneralAdmissionOnly}
      showDefaultMapColors={showDefaultMapColors}
      colorBandProps={colorBandProps}
      isZoomEnabled={isZoomEnabled}
      hideControls={hideControls}
      isPanEnabled={isPanEnabled}
      isWhiteBackground={isWhiteBackground}
      isRelocatedStaticInfo={isRelocatedStaticInfo}
      getColor={getColor}
      isHeatMap={isHeatMap}
      cornerContent={cornerContent}
      statsContent={statsContent}
      allowDrawLine={allowDrawLine}
      disableHoverEffect={disableHoverEffect}
      isCondensedView={isCondensedView}
      mapActionContainerClassname={mapActionContainerClassname}
      condensedControl={condensedControl}
    />
  ) : (
    <VenueMap
      svgMapUrl={VenueMapUrl.replace(
        VenueMapConfigPlaceholder,
        venueMapInfo.venueCfgId.toString()
      ).replace(
        VenueMapVersionPlaceholder,
        (venueMapInfo.svgMapVer ?? 1).toString()
      )}
      venueConfigId={venueMapInfo.venueCfgId}
      focalPoint={venueMapInfo.focalPt}
      availableSections={venueMapInfo.sections ?? []}
      selectedSections={selectedSectionData}
      markedSectionIds={markedSectionIds}
      animatedSectionIds={animatedSectionIds}
      onSectionClicked={onSectionClicked}
      onSectionTapAndHold={onSectionTapAndHold}
      onSectionHovered={onSectionHovered}
      setSelectedSections={setSelectedSections}
      staticInfoContent={staticInfoContent}
      staticInfoContentPosition={staticInfoContentPosition}
      infoIconContent={infoIconContent}
      isGeneralAdmissionOnly={isGeneralAdmissionOnly}
      showDefaultMapColors={showDefaultMapColors}
      colorBandProps={colorBandProps}
      isZoomEnabled={isZoomEnabled}
      hideControls={hideControls}
      isPanEnabled={isPanEnabled}
      isWhiteBackground={isWhiteBackground}
      isRelocatedStaticInfo={isRelocatedStaticInfo}
      isCondensedView={isCondensedView}
      mapActionContainerClassname={mapActionContainerClassname}
      getColor={getColor}
      isHeatMap={isHeatMap}
      cornerContent={cornerContent}
      statsContent={statsContent}
      allowDrawLine={allowDrawLine}
      disableHoverEffect={disableHoverEffect}
      condensedControl={condensedControl}
    />
  );
};
